import { BulkEmployeeActionBar } from '@/components/BulkEmployeeActionBar';
import { EmployeeAccessLevelInfoDialog } from '@/components/EmployeeAccessLevelInfoDialog';
import {
  EmployeeFilter,
  EmployeeFilterDrawer,
  EMPTY_EMPLOYEE_FILTER,
} from '@/components/EmployeeFilterDrawer';
import { TrainingManagerPermissionsDialog } from '@/components/TrainingManagerPermissionsDialog';
import { KLAYO_COLORS } from '@/themes';
import { useMediaQuery } from '@mui/material';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { ConfirmationDialog, Delayed, ManageEmployeesEvent } from 'klayowebshared';
import { isEmpty } from 'lodash';
import React from 'react';
import { AppContext } from '../../common/AppContext';
import axiosClient from '../../common/AxiosClient';
import { ErrorModel } from '../../common/models/ErrorModel';
import { track } from '../../common/track';
import { Button } from '../../components/Button';
import { AddEmployeeDialog } from '../../components/dialogs/AddEmployeeDialog';
import { NewEmployeeButton } from '../../components/NewEmployeeButton';
import { Paper } from '../../components/Paper';
import { Snackbar } from '../../components/Snackbar';
import { EmployeeTable } from '../../components/table/EmployeeTable';
import { TextField } from '../../components/TextField';
import { ViewComponent } from '../../components/ViewComponent';
import {
  EMPLOYEE_FILTER_ACCESS_OPTIONS,
  EMPLOYEE_FILTER_ROLE_OPTIONS,
  EMPLOYEE_FILTER_STATUS_OPTIONS,
  TableSearchDebounceTime,
} from '../../constants';
import { Data } from '../../data/Data';
import { SettingsAttribute } from '../../data/settings/SettingsAttribute';
import { SettingsEmployee } from '../../data/settings/SettingsEmployee';
import { SettingsEmployeeList } from '../../data/settings/SettingsEmployeeList';
import { withLocale, WithLocaleProps } from '../../providers/LocaleProvider';
import FilterIcon from '../../resources/images/icons-filter.svg';
import InfoCircleIcon from '../../resources/images/icons-info-circle.svg';
import RemoveIcon from '../../resources/images/icons-remove.svg';
import SearchIcon from '../../resources/images/icons-search.svg';
import { UploadService } from '../../services/upload.service';
import { getSortByApiValue } from '../../utilities';
import { AddEmployeeForm } from '../../utilities/addEmployeeSchema';
import { AddEditEmployeeView } from './AddEditEmployeeView';

interface SettingsEmployeesViewBaseProps extends WithLocaleProps {
  allowAssignAdminBilling?: boolean;
  organization: any;
  integration: any;
  theme: any;
  user: any;
  location: any;
  history: any;
  managers: any;
  employees: any;
  orgSettings: any;
  attributes: any;
  categories: any;
  onLoadEmployees: any;
  onLoadEmployeesPaging: any;
  onLoadAttributes: any;
  onLoadIntegration: any;
  onLoadCategories: any;
  onLoadEmployeesPageSize: any;
  onLoadJobs: any;
  onChangeCurrentUserFromSettings: any;
  onDataChanged: any;
  onShowNewCategoryDialog: any;
  onShowNewAttributeDialog: any;
  onLoadManagers: any;
  onNavigation: any;
  onBlockNavigation: any;
  onAllowNavigation: any;
  [key: string]: unknown;
}

class SettingsEmployeesViewBase extends ViewComponent<SettingsEmployeesViewBaseProps> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;
  debounceTimer: any;

  static defaultProps = {
    allowAssignAdminBilling: true,
  };

  constructor(props: SettingsEmployeesViewBaseProps) {
    super(props);

    this.state = {
      ...(ViewComponent as any).state,
      search: null,
      orderByConvertList: [{ tableFieldName: 'profilePicFullName', apiFieldName: 'firstName' }],
      sortModel: [
        {
          field: 'profilePicFullName',
          sort: 'asc',
        },
      ],
      employeeFilter: EMPTY_EMPLOYEE_FILTER as EmployeeFilter,
      showFilterDrawer: false,
      actionTarget: null,
      actionEmployee: null,
      saveEmployeeError: null,
      downloadingDoc: null,
      paginationModel: {
        page: 0,
        pageSize: SettingsEmployeeList.defaultPageSize,
      },
      isEmployeesLoading: false,
      isOpenTrainingManagerPermissionsDialog: false,
      trainingManagerConfigurations: [],
      selectedTrainingManagerConfiguration: null,
      isAssignTrainingManagerLoading: false,
      isUnassignTrainingManagerLoading: false,
      accessLevelChange: null,
      isChangingAccessLevel: false,
      selectedRowIds: [] as GridRowSelectionModel,
      showAccessLevelInfoDialog: false,
      showAddEmployeeDialog: false,
      prefillEmployee: null as AddEmployeeForm | null,
    };

    this.debounceTimer = null;
  }

  componentDidMount() {
    const { onLoadEmployees, onLoadAttributes, onLoadIntegration, onLoadCategories } = this.props;
    const { attributes, integration, categories } = this.props;

    track(ManageEmployeesEvent.Viewed);

    this.context.eventBus?.on('employees', (isLoading: boolean) => {
      this.setState({ isEmployeesLoading: isLoading });
    });

    if (!attributes) onLoadAttributes();
    if (!categories) onLoadCategories();
    onLoadEmployees();
    if (!integration) onLoadIntegration();
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
  }

  componentDidUpdate(prevProps: any) {
    const prevPath = prevProps.location?.pathname ?? '';
    const currPath = (this.props as any).location?.pathname ?? '';
    if (
      prevPath.startsWith('/settings/employees/new') &&
      !currPath.startsWith('/settings/employees/new') &&
      this.state.prefillEmployee !== null
    ) {
      this.setState({ prefillEmployee: null });
    }
  }

  onViewChange(location: any) {
    if (this.pathEquals('/settings/employees')) this.resetErrors();
  }

  resetErrors() {
    this.setState({ saveEmployeeError: null });
  }

  getFilterApiParams() {
    const { employeeFilter } = this.state;
    return {
      status: employeeFilter.status,
      includeInactive: employeeFilter.includeInactive,
      accessLevels: employeeFilter.accessLevels,
      roles: employeeFilter.roles,
    };
  }

  onSearch(e: any) {
    const { onLoadEmployees } = this.props;
    const {
      sortModel,
      orderByConvertList,
      paginationModel: { pageSize },
    } = this.state;
    const [{ field, sort }] = sortModel;
    const orderBy = getSortByApiValue(orderByConvertList, field, sort);
    const { status, includeInactive, accessLevels, roles } = this.getFilterApiParams();
    const keyword = e.target.value;
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      if (keyword) track(ManageEmployeesEvent.Search, { searchtext: keyword });
      onLoadEmployees(
        true,
        keyword,
        pageSize,
        status,
        orderBy,
        includeInactive,
        accessLevels,
        roles
      );
    }, TableSearchDebounceTime);
    this.setState({ search: e.target.value, paginationModel: { page: 0, pageSize } });
  }

  onPaginationModelChange(model: any) {
    const { page, pageSize } = model;
    this.setState({ paginationModel: model });
    const { onLoadEmployeesPageSize } = this.props;
    const { search, sortModel, orderByConvertList } = this.state;
    const [{ field, sort }] = sortModel;
    const orderBy = getSortByApiValue(orderByConvertList, field, sort);
    const { status, includeInactive, accessLevels, roles } = this.getFilterApiParams();
    onLoadEmployeesPageSize(
      page + 1,
      pageSize,
      search,
      status,
      orderBy,
      includeInactive,
      accessLevels,
      roles
    );
  }

  onApplyFilter(filter: EmployeeFilter) {
    const { onLoadEmployees } = this.props;
    const {
      search,
      sortModel,
      orderByConvertList,
      paginationModel: { pageSize },
    } = this.state;
    track(ManageEmployeesEvent.FilterSet, {
      filters: {
        status: filter.status,
        includeInactive: filter.includeInactive,
        accessLevels: filter.accessLevels,
        roles: filter.roles,
      },
    });
    const [{ field, sort }] = sortModel;
    const orderBy = getSortByApiValue(orderByConvertList, field, sort);
    this.setState({ employeeFilter: filter, paginationModel: { page: 0, pageSize } });
    onLoadEmployees(
      true,
      search,
      pageSize,
      filter.status,
      orderBy,
      filter.includeInactive,
      filter.accessLevels,
      filter.roles
    );
  }

  onSortModelChange(sortModel: any) {
    const { onLoadEmployees } = this.props;
    const {
      search,
      orderByConvertList,
      paginationModel: { pageSize },
    } = this.state;
    const [{ field, sort }] = sortModel;
    const orderBy = getSortByApiValue(orderByConvertList, field, sort);
    const { status, includeInactive, accessLevels, roles } = this.getFilterApiParams();
    onLoadEmployees(true, search, pageSize, status, orderBy, includeInactive, accessLevels, roles);
    this.setState({ sortModel, paginationModel: { page: 0, pageSize } });
  }

  onEmployeeAction(e: any, employee: any) {
    this.setState({ actionTarget: e.target, actionEmployee: employee });
  }

  onEmployeeSelect(e: any, employee: any) {
    track(ManageEmployeesEvent.EmployeeRowClick);
    this.props.history.push('/settings/employees/edit/' + employee.employeeId);
  }

  onEditEmployee() {
    const { actionEmployee } = this.state;
    this.onCloseActionMenu();

    this.props.history.push('/settings/employees/edit/' + actionEmployee.employeeId);
  }

  onChangeEmployeeStatus(status: any) {
    const { employees, onLoadEmployees } = this.props;
    const { actionEmployee } = this.state;

    this.onCloseActionMenu();
    this.context?.setLoading?.('deleteEmployee', true);

    const targetStatus = status.index;

    axiosClient
      .put('/Employee/Status', {
        employeeId: actionEmployee.employeeId,
        status: targetStatus,
      })
      .then((response: any) => {
        actionEmployee.setStatus(targetStatus);
        this.setState({
          successSnackbar: this.props.translate(
            targetStatus === SettingsEmployee.status.inactive.index
              ? 'settingsViews.employees.messages.inactive'
              : 'settingsViews.employees.messages.active'
          ),
        });
        employees.update(actionEmployee);
        this.reloadEmployees();
      })
      .finally(() => {
        this.context?.setLoading?.('deleteEmployee', false);
      });
  }

  onSendInvite() {
    const { actionEmployee } = this.state;

    this.onCloseActionMenu();
    this.context?.setLoading?.('deleteEmployee', true);

    // No optimistic status write: the server decides whether the invite changes status at all
    // (an already-active employee keeps Active), so guessing "Invited" here can render a status
    // that never happened. reloadEmployees() below is the source of truth.
    axiosClient
      .post('/Employee/ResendInvite', {
        employeeId: actionEmployee.employeeId,
      })
      .then((response: any) => {
        this.setState({
          successSnackbar: this.props.translate('settingsViews.employees.invitationSent'),
        });
        this.reloadEmployees();
      })
      .finally(() => {
        this.context?.setLoading?.('deleteEmployee', false);
      });
  }

  onCloseActionMenu() {
    this.setState({ actionTarget: null, actionEmployee: null });
  }

  trackMenuOption(optionInfo: string) {
    track(ManageEmployeesEvent.MenuOption, { optionInfo });
  }

  async onSaveEmployee(employee: any, callback: any) {
    const {
      user,
      employees,
      onLoadEmployees,
      onLoadJobs,
      onChangeCurrentUserFromSettings,
      onLoadAttributes,
      onDataChanged,
    } = this.props;

    this.resetErrors();

    try {
      this.context?.setLoading?.('saveEmployee', true);

      await this.uploadAttributeFiles(employee?.attributes);
      await this.uploadTrainingFiles(employee?.trainings);

      if (!employee.employeeId) {
        const response: any = await axiosClient.post('/Employee', employee.toApi());

        employee.employeeId = response.data.employee.employeeId;
        employee.rowId = response.data.employee.employeeId;
        employee.id = response.data.employee.employeeId;
        employees.add(employee);
        track(ManageEmployeesEvent.EmployeeAdded, {
          access: employee.isFullAccess ? 'full' : 'limited',
        });
        this.reloadEmployees();
        if (onLoadJobs) onLoadJobs(true);
        if (onLoadAttributes) onLoadAttributes(true);
        if (onDataChanged) onDataChanged('employees');
        this.props.history.push('/settings/employees');
      } else {
        await axiosClient.put('/Employee', employee.toApi());
        employees.update(employee);
        this.reloadEmployees();
        if (onLoadJobs) onLoadJobs(true);
        if (onLoadAttributes) onLoadAttributes(true);
        if (onDataChanged) onDataChanged('employees');
        this.props.history.push('/settings/employees');

        if (employee.employeeId === user.employeeId && onChangeCurrentUserFromSettings)
          onChangeCurrentUserFromSettings(employee);
      }
    } catch (error) {
    } finally {
      this.context?.setLoading?.('saveEmployee', false);
      callback();
    }
  }

  async uploadAttributeFiles(uploadAttributeFiles: SettingsAttribute[]) {
    return await Promise.all(
      uploadAttributeFiles.map(async (a) => {
        if (!isEmpty(a.files)) {
          const uploadedIds = await UploadService.onUploadFiles(a.files);
          a.supportingEvidenceUploadIds = uploadedIds;
        }

        return a;
      })
    );
  }

  async uploadTrainingFiles(uploadTrainingFiles: SettingsAttribute[]) {
    return await Promise.all(
      uploadTrainingFiles.map(async (a) => {
        if (!isEmpty(a.files)) {
          const uploadedIds = await UploadService.onUploadFiles(a.files);
          a.supportingEvidenceUploadIds = uploadedIds;
        }

        return a;
      })
    );
  }

  onLoadPreviousAttributeActivity(attribute: any, callback: any) {
    if (attribute.isNew || !attribute.employeeAttributeId) {
      callback(attribute);
      return;
    }

    this.context?.setLoading?.('loadPreviousAttributeActivity', true);

    attribute
      .loadRequestActivity(this.context)
      .then((activity: any) => {
        callback(attribute);
      })
      .catch((e: any) => {})
      .finally(() => this.context?.setLoading?.('loadPreviousAttributeActivity', false));
  }

  onDocumentClick(e: any, doc: any) {
    this.context?.setLoading?.('downloadingDoc', true);
    this.setState({ downloadingDoc: doc });

    Data.call('get', '/EmployeeAttribute/download/' + doc.documentId, {
      responseType: 'blob',
    })
      .then((response: any) => {
        const blob = new Blob([response.data], { type: response.headers['content-type'] });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', doc.documentName);
        document.body.appendChild(link);
        link.click();
      })
      .catch((e: any) => {
        this.setState({ error: ErrorModel.parseServerError(e) });
      })
      .finally(() => this.context?.setLoading?.('downloadingDoc', false));
  }

  onDocumentDownloadSnackbarClose() {
    this.setState({ downloadingDoc: null });
  }

  async onAssignPermissions(
    e: any,
    actionEmployee: any,
    isOrganizationAdmin: any,
    isBilling: any,
    isInstructor?: any,
    trainingManagerConfigurationTypeId?: string,
    isAllowCloseActionMenu = true
  ) {
    const { user, employees, onLoadEmployees } = this.props;

    if (isAllowCloseActionMenu) {
      this.onCloseActionMenu();
    }

    this.context?.setLoading?.('assignPermissions', true);

    try {
      await axiosClient.put('/Employee/Permissions', {
        employeeId: actionEmployee.employeeId,
        isOrganizationAdmin: isOrganizationAdmin,
        isBilling: isBilling,
        isInstructor: isInstructor,
        ...(trainingManagerConfigurationTypeId !== null &&
        trainingManagerConfigurationTypeId !== undefined
          ? {
              trainingManagerConfigurationTypeId: trainingManagerConfigurationTypeId || undefined,
              isTrainingManager: !!trainingManagerConfigurationTypeId,
            }
          : {}),
      });

      if (user.employeeId === actionEmployee.employeeId) {
        if (isOrganizationAdmin !== null) user.isOrganizationAdmin = isOrganizationAdmin;
        if (isBilling !== null) user.isBillingUser = isBilling;
        if (trainingManagerConfigurationTypeId !== null) {
          user.trainingManagerConfigurationTypeId = trainingManagerConfigurationTypeId;
          user.isTrainingManager = !!trainingManagerConfigurationTypeId;
        }

        this.forceUpdate();
        // Redirect based on the permission the admin just changed on themselves, not their overall
        // role: assigning Billing takes them to the Billing page; otherwise keep org admins on the
        // Employees screen and send everyone else home. (Previously this keyed off
        // user.isBillingUser, so any billing user was always bounced to Billing regardless of which
        // permission they updated.)
        if (isBilling) this.props.history.push('/settings/billing');
        else if (user.isOrganizationAdmin) this.props.history.push('/settings/employees');
        else this.props.history.push('/');
      }

      employees.update(actionEmployee);
      this.reloadEmployees();
    } catch (error) {
    } finally {
      this.context?.setLoading?.('assignPermissions', false);
    }
  }

  getEmployeeId() {
    const { location } = this.props;
    const path = location.pathname;
    return path.includes('edit') ? path.replace('/settings/employees/edit/', '') : null;
  }

  getEmployee() {
    const { employees } = this.props;
    if (!employees) return null;
    const editEmployeeId = this.getEmployeeId();
    return employees.get(editEmployeeId);
  }

  onOpenTrainingManagerPermissionsDialog() {
    this.setState({
      isOpenTrainingManagerPermissionsDialog: true,
      actionTarget: null,
    });
  }

  onCloseTrainingManagerPermissionsDialog() {
    this.setState({
      isOpenTrainingManagerPermissionsDialog: false,
      actionEmployee: null,
    });
  }

  async onAssignTrainingManagerPermissions(trainingManagerConfigurationTypeId?: string) {
    try {
      this.setState({
        isAssignTrainingManagerLoading: !!trainingManagerConfigurationTypeId,
        isUnassignTrainingManagerLoading: !trainingManagerConfigurationTypeId,
      });
      await this.onAssignPermissions(
        null,
        this.state.actionEmployee,
        null,
        null,
        null,
        trainingManagerConfigurationTypeId,
        false
      );

      this.onCloseTrainingManagerPermissionsDialog();
    } catch (error) {
    } finally {
      this.setState({
        isAssignTrainingManagerLoading: false,
        isUnassignTrainingManagerLoading: false,
      });
    }
  }

  reloadEmployees() {
    const { onLoadEmployeesPaging } = this.props;
    const { search, paginationModel, sortModel, orderByConvertList } = this.state;
    const [{ field, sort }] = sortModel;
    const orderBy = getSortByApiValue(orderByConvertList, field, sort);
    const { status, includeInactive, accessLevels, roles } = this.getFilterApiParams();

    if (onLoadEmployeesPaging)
      onLoadEmployeesPaging(
        paginationModel.page + 1,
        paginationModel.pageSize,
        search,
        status,
        orderBy,
        includeInactive,
        accessLevels,
        roles
      );
  }

  onRowSelectionModelChange(model: GridRowSelectionModel) {
    if ((model as any[]).length > (this.state.selectedRowIds as any[]).length) {
      track(ManageEmployeesEvent.EmployeeSelected);
    }
    this.setState({ selectedRowIds: model });
  }

  async onBulkActivate(targets: SettingsEmployee[]) {
    if (!targets.length) return;
    track(ManageEmployeesEvent.EmployeeSelectionAction, { action: 'activate' });
    this.context?.setLoading?.('bulkAction', true);
    try {
      await axiosClient.put('/Employee/Status/Bulk', {
        employeeIds: targets.map((e) => e.employeeId),
        status: SettingsEmployee.status.active.index,
      });
      this.setState({
        selectedRowIds: [],
        successSnackbar: this.props.translate(
          targets.length === 1
            ? 'settingsViews.employees.messages.bulkActivatedOne'
            : 'settingsViews.employees.messages.bulkActivated',
          { count: targets.length }
        ),
      });
      this.reloadEmployees();
    } catch (error) {
    } finally {
      this.context?.setLoading?.('bulkAction', false);
    }
  }

  async onBulkDeactivate(targets: SettingsEmployee[]) {
    if (!targets.length) return;
    track(ManageEmployeesEvent.EmployeeSelectionAction, { action: 'deactivate' });
    this.context?.setLoading?.('bulkAction', true);
    try {
      await axiosClient.put('/Employee/Status/Bulk', {
        employeeIds: targets.map((e) => e.employeeId),
        status: SettingsEmployee.status.inactive.index,
      });
      this.setState({
        selectedRowIds: [],
        successSnackbar: this.props.translate(
          targets.length === 1
            ? 'settingsViews.employees.messages.bulkDeactivatedOne'
            : 'settingsViews.employees.messages.bulkDeactivated',
          { count: targets.length }
        ),
      });
      this.reloadEmployees();
    } catch (error) {
    } finally {
      this.context?.setLoading?.('bulkAction', false);
    }
  }

  async onBulkUpgradeToFull(targets: SettingsEmployee[]) {
    if (!targets.length) return;
    track(ManageEmployeesEvent.EmployeeSelectionAction, { action: 'upgrade' });
    this.context?.setLoading?.('bulkAction', true);
    try {
      await axiosClient.put('/Employee/AccessLevel/Bulk', {
        employeeIds: targets.map((e) => e.employeeId),
        accessLevel: 1,
      });
      this.setState({
        selectedRowIds: [],
        successSnackbar: this.props.translate(
          targets.length === 1
            ? 'settingsViews.employees.messages.bulkUpgradedOne'
            : 'settingsViews.employees.messages.bulkUpgraded',
          { count: targets.length }
        ),
      });
      this.reloadEmployees();
    } catch (error) {
    } finally {
      this.context?.setLoading?.('bulkAction', false);
    }
  }

  async onBulkDowngradeToLimited(targets: SettingsEmployee[]) {
    if (!targets.length) return;
    track(ManageEmployeesEvent.EmployeeSelectionAction, { action: 'downgrade' });
    this.context?.setLoading?.('bulkAction', true);
    try {
      await axiosClient.put('/Employee/AccessLevel/Bulk', {
        employeeIds: targets.map((e) => e.employeeId),
        accessLevel: 0,
      });
      this.setState({
        selectedRowIds: [],
        successSnackbar: this.props.translate(
          targets.length === 1
            ? 'settingsViews.employees.messages.bulkDowngradedOne'
            : 'settingsViews.employees.messages.bulkDowngraded',
          { count: targets.length }
        ),
      });
      this.reloadEmployees();
    } catch (error) {
    } finally {
      this.context?.setLoading?.('bulkAction', false);
    }
  }

  async onBulkResendInvite(targets: SettingsEmployee[]) {
    if (!targets.length) return;
    track(ManageEmployeesEvent.EmployeeSelectionAction, { action: 'invite' });
    this.context?.setLoading?.('bulkAction', true);
    try {
      const response: any = await axiosClient.post('/Employee/ResendInvite/Bulk', {
        employeeIds: targets.map((e) => e.employeeId),
      });
      // Report what the server actually sent, not what was selected. The server skips employees it
      // will not mail (not found, no email, and now excluded from bulk invites); claiming the
      // selected count would tell an admin an invite went out when it deliberately did not.
      // Falls back to 0, not targets.length: if the response shape ever changes, reporting nothing
      // sent is visibly wrong, whereas silently reasserting the selected count hides the change.
      const sentCount: number = response?.data?.sentCount ?? 0;
      this.setState({
        selectedRowIds: [],
        successSnackbar: this.props.translate(
          sentCount === 1
            ? 'settingsViews.employees.messages.bulkInvitedOne'
            : 'settingsViews.employees.messages.bulkInvited',
          { count: sentCount }
        ),
      });
      this.reloadEmployees();
    } catch (error) {
    } finally {
      this.context?.setLoading?.('bulkAction', false);
    }
  }

  async onQuickAddEmployee(data: AddEmployeeForm) {
    const { employees } = this.props;
    const response: any = await axiosClient.post('/Employee', {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      isFullAccess: false,
      isOrganizationAdmin: false,
      isBilling: false,
    });
    employees.add(response.data.employee);
    track(ManageEmployeesEvent.EmployeeAdded, { access: 'limited' });
    this.reloadEmployees();
  }

  onChangeToFullAccess(data: AddEmployeeForm) {
    this.setState({ showAddEmployeeDialog: false, prefillEmployee: data }, () => {
      (this.props as any).history.push('/settings/employees/new');
    });
  }

  onRequestChangeAccessLevel(toFullAccess: boolean) {
    const { actionEmployee } = this.state;
    this.onCloseActionMenu();
    this.setState({ accessLevelChange: { employee: actionEmployee, toFullAccess } });
  }

  onCancelChangeAccessLevel() {
    this.setState({ accessLevelChange: null });
  }

  async onConfirmChangeAccessLevel() {
    const { user } = this.props;
    const change = this.state.accessLevelChange;
    if (!change) return;

    this.setState({ isChangingAccessLevel: true });
    this.context?.setLoading?.('changeAccessLevel', true);

    try {
      await axiosClient.put('/Employee/AccessLevel', {
        employeeId: change.employee.employeeId,
        accessLevel: change.toFullAccess ? 1 : 0,
      });

      if (user && user.employeeId === change.employee.employeeId) {
        user.isFullAccess = change.toFullAccess;
        this.forceUpdate();
      }

      this.setState({
        successSnackbar: this.props.translate(
          change.toFullAccess
            ? 'settingsViews.employees.messages.upgraded'
            : 'settingsViews.employees.messages.downgraded'
        ),
        accessLevelChange: null,
      });

      this.reloadEmployees();
    } catch (error) {
      this.setState({ accessLevelChange: null });
    } finally {
      this.setState({ isChangingAccessLevel: false });
      this.context?.setLoading?.('changeAccessLevel', false);
    }
  }

  render() {
    const {
      organization,
      integration,
      onLoadIntegration,
      theme,
      user,
      location,
      history,
      managers,
      onShowNewCategoryDialog,
      onShowNewAttributeDialog,
      onLoadAttributes,
      employees,
      onDataChanged,
      onLoadManagers,
      orgSettings,
      attributes,
      allowAssignAdminBilling,
      onNavigation,
      onBlockNavigation,
      onAllowNavigation,
    } = this.props;
    const {
      search,
      sortModel,
      actionTarget,
      actionEmployee,
      saveEmployeeError,
      downloadingDoc,
      paginationModel,
      isEmployeesLoading,
      selectedRowIds,
      showAccessLevelInfoDialog,
      showFilterDrawer,
      employeeFilter,
    } = this.state;

    const path = location.pathname;
    const editMode = path.includes('edit');
    const showAddEditEmployeeView =
      path.startsWith('/settings/employees/new') || path.startsWith('/settings/employees/edit');

    const editEmployee = this.getEmployee();
    const editEmployeeId = this.getEmployeeId();

    const filterItems = [];
    if (search && search !== '')
      filterItems.push({
        id: 'search',
        field: 'profilePicFullName',
        operator: 'contains',
        value: search,
      });

    return (
      <div>
        {!showAddEditEmployeeView ? (
          <Paper theme={theme} padding={{ xs: '46px 24px', md: '60px' }} borderFromBreakpoint='md'>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1}
              sx={{ mb: 1 }}
            >
              <h1 style={{ margin: 0 }}>{this.props.translate('settingsViews.employees.title')}</h1>
              {employees && (
                <Stack direction='row' alignItems='center' spacing={0.5}>
                  <Typography variant='body2' color={KLAYO_COLORS.GreyGR600}>
                    {this.props.translate('settingsViews.employees.fullSeatsPrefix', {
                      used: employees.fullAccessSeatCount,
                    })}{' '}
                    <strong>{employees.planEmployeeCap}</strong>{' '}
                    {this.props.translate('settingsViews.employees.fullSeatsUsedSuffix')}
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => {
                      track(ManageEmployeesEvent.FullAccessInfo);
                      this.setState({ showAccessLevelInfoDialog: true });
                    }}
                    sx={{
                      '& svg path[fill="#1E88E5"]': {
                        fill: KLAYO_COLORS.NeonBlueNB500,
                      },
                    }}
                  >
                    <InfoCircleIcon width={20} height={20} />
                  </IconButton>
                </Stack>
              )}
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              flexWrap='wrap'
              spacing={2}
              sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)', padding: '30px 0' }}
            >
              <Stack direction='row' spacing={2} flex={1}>
                <TextField
                  value={search}
                  dense={true}
                  placeholder={this.props.translate('placeholders.searchEmployees')}
                  disabled={user === null}
                  fullWidth={true}
                  autoComplete={false}
                  leadingIcon={<SearchIcon />}
                  onChange={this.onSearch.bind(this)}
                  sx={{ maxWidth: { md: '300px' }, flex: 1 }}
                />
                <Button
                  size='md'
                  theme={theme}
                  variant='outlined'
                  startIcon={<FilterIcon />}
                  label={this.props.translate('common.filters')}
                  onClick={() => this.setState({ showFilterDrawer: true })}
                />
              </Stack>
              <NewEmployeeButton
                translate={this.props.translate}
                theme={theme}
                onSelectFullAccess={() => {
                  track(ManageEmployeesEvent.NewEmployeeFull);
                  (this.props as any).history.push('/settings/employees/new');
                }}
                onSelectLimitedAccess={() => {
                  track(ManageEmployeesEvent.NewEmployeeLimited);
                  this.setState({ showAddEmployeeDialog: true });
                }}
              />
            </Stack>
            {(employeeFilter.status !== null ||
              employeeFilter.includeInactive ||
              employeeFilter.accessLevels.length > 0 ||
              employeeFilter.roles.length > 0) && (
              <Stack direction='row' gap={1} flexWrap='wrap' alignItems='center' sx={{ pb: 2 }}>
                {employeeFilter.status !== null && (
                  <Chip
                    label={
                      <Stack direction='row' gap={0.5} alignItems='center'>
                        <Typography variant='body2' color={KLAYO_COLORS.GreyGR900}>
                          {this.props.translate('common.status')}:
                        </Typography>
                        <Typography variant='body2' fontWeight={600} color={KLAYO_COLORS.GreyGR900}>
                          {this.props.translate(
                            EMPLOYEE_FILTER_STATUS_OPTIONS.find(
                              (o) => o.value === String(employeeFilter.status)
                            )?.labelKey ?? ''
                          )}
                        </Typography>
                      </Stack>
                    }
                    onDelete={() =>
                      this.onApplyFilter({
                        ...employeeFilter,
                        status: null,
                        includeInactive: false,
                      })
                    }
                    deleteIcon={<RemoveIcon />}
                    variant='outlined'
                  />
                )}
                {employeeFilter.includeInactive && (
                  <Chip
                    label={
                      <Typography variant='body2' color={KLAYO_COLORS.GreyGR900}>
                        {this.props.translate('settingsViews.employees.includeInactive')}
                      </Typography>
                    }
                    onDelete={() =>
                      this.onApplyFilter({ ...employeeFilter, includeInactive: false })
                    }
                    deleteIcon={<RemoveIcon />}
                    variant='outlined'
                  />
                )}
                {employeeFilter.accessLevels.map((level: number) => {
                  const opt = EMPLOYEE_FILTER_ACCESS_OPTIONS.find((o) => o.value === level);
                  return opt ? (
                    <Chip
                      key={`access-${level}`}
                      label={
                        <Stack direction='row' gap={0.5} alignItems='center'>
                          <Typography variant='body2' color={KLAYO_COLORS.GreyGR900}>
                            {this.props.translate('common.access')}:
                          </Typography>
                          <Typography
                            variant='body2'
                            fontWeight={600}
                            color={KLAYO_COLORS.GreyGR900}
                          >
                            {this.props.translate(opt.labelKey)}
                          </Typography>
                        </Stack>
                      }
                      onDelete={() =>
                        this.onApplyFilter({
                          ...employeeFilter,
                          accessLevels: employeeFilter.accessLevels.filter(
                            (a: number) => a !== level
                          ),
                        })
                      }
                      deleteIcon={<RemoveIcon />}
                      variant='outlined'
                    />
                  ) : null;
                })}
                {employeeFilter.roles.map((role: number) => {
                  const opt = EMPLOYEE_FILTER_ROLE_OPTIONS.find((o) => o.value === role);
                  return opt ? (
                    <Chip
                      key={`role-${role}`}
                      label={
                        <Stack direction='row' gap={0.5} alignItems='center'>
                          <Typography variant='body2' color={KLAYO_COLORS.GreyGR900}>
                            {this.props.translate('common.role')}:
                          </Typography>
                          <Typography
                            variant='body2'
                            fontWeight={600}
                            color={KLAYO_COLORS.GreyGR900}
                          >
                            {this.props.translate(opt.labelKey)}
                          </Typography>
                        </Stack>
                      }
                      onDelete={() =>
                        this.onApplyFilter({
                          ...employeeFilter,
                          roles: employeeFilter.roles.filter((r: number) => r !== role),
                        })
                      }
                      deleteIcon={<RemoveIcon />}
                      variant='outlined'
                    />
                  ) : null;
                })}
                <Typography
                  variant='body2'
                  sx={{ color: KLAYO_COLORS.NeonBlueNB500, cursor: 'pointer', ml: 1 }}
                  onClick={() => this.onApplyFilter(EMPTY_EMPLOYEE_FILTER)}
                >
                  {this.props.translate('common.clearAll')}
                </Typography>
              </Stack>
            )}
            <Delayed>
              <CheckboxEmployeeTable
                rowHasAction={() => true}
                minHeight='300px'
                theme={theme}
                translate={this.props.translate}
                showHeaderFromBreakpoint='md'
                filterMode='server'
                filterItems={filterItems}
                dense={true}
                paginationMode='server'
                totalCount={this.props.employees?.totalCount}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                paginationModel={paginationModel}
                onPaginationModelChange={this.onPaginationModelChange.bind(this)}
                columns={[
                  {
                    type: EmployeeTable.columns.fullName,
                    label: this.props.translate('tableColumns.name'),
                    visibleToBreakpoint: 'md',
                  },
                  {
                    type: EmployeeTable.columns.profilePicFullName,
                    label: this.props.translate('tableColumns.employee'),
                    visibleFromBreakpoint: 'md',
                  },
                  {
                    type: EmployeeTable.columns.status,
                    label: this.props.translate('tableColumns.status'),
                    visibleFromBreakpoint: 'md',
                  },
                  {
                    type: EmployeeTable.columns.accessLevel,
                    label: this.props.translate('tableColumns.accessLevel'),
                    visibleFromBreakpoint: 'md',
                  },
                  {
                    type: EmployeeTable.columns.actions,
                    label: this.props.translate('tableColumns.actions'),
                  },
                ]}
                sortingMode='server'
                sortModel={sortModel}
                onSortModelChange={this.onSortModelChange.bind(this)}
                onEmployeeSelect={this.onEmployeeSelect.bind(this)}
                onEmployeeAction={this.onEmployeeAction.bind(this)}
                paper={false}
                rows={employees ? employees.employees : []}
                isLoading={isEmployeesLoading}
                keyword={search}
                noRowsMessage={this.props.translate('common.noEmployeesYet')}
                rowSelectionModel={selectedRowIds as GridRowSelectionModel}
                onRowSelectionModelChange={this.onRowSelectionModelChange.bind(this)}
                selectable={false}
              />
            </Delayed>

            {actionEmployee && (
              <Menu
                open={actionTarget !== null}
                anchorEl={actionTarget}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                onClose={this.onCloseActionMenu.bind(this)}
              >
                <MenuItem
                  onClick={() => {
                    this.trackMenuOption('edit');
                    this.onEditEmployee();
                  }}
                >
                  <ListItemText>
                    {this.props.translate('settingsViews.employees.editEmployee')}
                  </ListItemText>
                </MenuItem>

                {actionEmployee.status === SettingsEmployee.status.inactive && (
                  <>
                    <Divider />
                    <MenuItem
                      onClick={(e) => {
                        this.trackMenuOption('activate');
                        this.onChangeEmployeeStatus(SettingsEmployee.status.active);
                      }}
                    >
                      <ListItemText>
                        {this.props.translate('settingsViews.employees.activateEmployee')}
                      </ListItemText>
                    </MenuItem>
                  </>
                )}

                {[
                  SettingsEmployee.status.active,
                  SettingsEmployee.status.invited,
                  SettingsEmployee.status.added,
                ].includes(actionEmployee.status) && (
                  <>
                    <Divider />
                    <MenuItem
                      disabled={!employees.canDeactivate(actionEmployee)}
                      onClick={(e) => {
                        this.trackMenuOption('deactivate');
                        this.onChangeEmployeeStatus(SettingsEmployee.status.inactive);
                      }}
                    >
                      <ListItemText>
                        {this.props.translate('settingsViews.employees.deactivateEmployee')}
                      </ListItemText>
                    </MenuItem>
                    {actionEmployee.isFullAccess ? (
                      <MenuItem
                        onClick={() => {
                          this.trackMenuOption('downgrade');
                          this.onRequestChangeAccessLevel(false);
                        }}
                      >
                        <ListItemText>
                          {this.props.translate('settingsViews.employees.downgradeToLimited')}
                        </ListItemText>
                      </MenuItem>
                    ) : (
                      <MenuItem
                        disabled={
                          employees.planEmployeeCap > 0 &&
                          employees.fullAccessSeatCount >= employees.planEmployeeCap
                        }
                        onClick={() => {
                          this.trackMenuOption('upgrade');
                          this.onRequestChangeAccessLevel(true);
                        }}
                      >
                        <ListItemText>
                          {this.props.translate('settingsViews.employees.upgradeToFull')}
                        </ListItemText>
                      </MenuItem>
                    )}
                  </>
                )}

                {actionEmployee.status === SettingsEmployee.status.added && (
                  <>
                    <Divider />
                    <MenuItem
                      onClick={(e) => {
                        this.trackMenuOption('send_invite');
                        this.onSendInvite();
                      }}
                    >
                      <ListItemText>
                        {this.props.translate('settingsViews.employees.sendInvite')}
                      </ListItemText>
                    </MenuItem>
                  </>
                )}

                {actionEmployee.status === SettingsEmployee.status.invited && (
                  <>
                    <Divider />
                    <MenuItem
                      onClick={(e) => {
                        this.trackMenuOption('resend_invite');
                        this.onSendInvite();
                      }}
                    >
                      <ListItemText>
                        {this.props.translate('settingsViews.employees.resendInvite')}
                      </ListItemText>
                    </MenuItem>
                  </>
                )}

                {allowAssignAdminBilling && (
                  <>
                    <Divider />
                    <MenuItem
                      disabled={
                        actionEmployee.isOrganizationAdmin &&
                        !employees.canUnassignAdmin(actionEmployee)
                      }
                      onClick={(e) => {
                        this.trackMenuOption(
                          actionEmployee.isOrganizationAdmin ? 'unassign_admin' : 'assign_admin'
                        );
                        this.onAssignPermissions(
                          e,
                          actionEmployee,
                          !actionEmployee.isOrganizationAdmin,
                          null
                        );
                      }}
                    >
                      <ListItemText>
                        {actionEmployee.isOrganizationAdmin
                          ? this.props.translate('permissions.unassignAdmin')
                          : this.props.translate('permissions.assignAdmin')}
                      </ListItemText>
                    </MenuItem>
                    <MenuItem
                      disabled={
                        !user.isBillingUser ||
                        (actionEmployee.isBilling && !employees.canUnassignBilling(actionEmployee))
                      }
                      onClick={(e) => {
                        this.trackMenuOption(
                          actionEmployee.isBilling ? 'unassign_billing' : 'assign_billing'
                        );
                        this.onAssignPermissions(
                          e,
                          actionEmployee,
                          null,
                          !actionEmployee.isBilling
                        );
                      }}
                    >
                      <ListItemText>
                        {actionEmployee.isBilling
                          ? this.props.translate('permissions.unassignBilling')
                          : this.props.translate('permissions.assignBilling')}
                      </ListItemText>
                    </MenuItem>
                  </>
                )}
                <MenuItem
                  disabled={!actionEmployee.isFullAccess}
                  onClick={(e) => {
                    this.trackMenuOption(
                      actionEmployee.isInstructor ? 'unassign_instructor' : 'assign_instructor'
                    );
                    this.onAssignPermissions(
                      e,
                      actionEmployee,
                      null,
                      null,
                      !actionEmployee.isInstructor
                    );
                  }}
                >
                  <ListItemText>
                    {actionEmployee.isInstructor
                      ? this.props.translate('permissions.unassignInstructor')
                      : this.props.translate('permissions.assignInstructor')}
                  </ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={(e) => {
                    this.trackMenuOption('training_manager');
                    this.onOpenTrainingManagerPermissionsDialog();
                  }}
                >
                  <ListItemText>
                    {actionEmployee.isTrainingManager
                      ? this.props.translate('permissions.editAsTrainingManager')
                      : this.props.translate('permissions.assignTrainingManager')}
                  </ListItemText>
                </MenuItem>
              </Menu>
            )}
          </Paper>
        ) : (
          <AddEditEmployeeView
            {...{
              ...this.props,
              className: 'klayo__add-edit-employee-setting',
              user,
              organization,
              theme,
              editMode,
              employee: editMode
                ? editEmployee
                : new SettingsEmployee(this.state.prefillEmployee ?? undefined),
              employeeId: editEmployeeId,
              integration,
              onLoadIntegration,
              error: saveEmployeeError,
              onShowNewAttributeDialog,
              onShowNewCategoryDialog,
              onLoadAttributes,
              onLoadManagers,
              onDataChanged,
              onLoadPreviousAttributeActivity: this.onLoadPreviousAttributeActivity.bind(this),
              onDocumentClick: this.onDocumentClick.bind(this),
              attributes,
              managers,
              locations: orgSettings ? orgSettings.locations : null,
              integrations: orgSettings ? orgSettings.organizationIntegrations : null,
              onCancel: (e: any) => history.push('/settings/employees'),
              onSave: this.onSaveEmployee.bind(this),
              onBlockNavigation,
              onAllowNavigation,
              onNavigation,
            }}
          />
        )}

        {this.renderSnackbar()}

        {downloadingDoc && (
          <Snackbar
            open={downloadingDoc}
            duration={6000}
            type='info'
            onClose={this.onDocumentDownloadSnackbarClose.bind(this)}
            message={this.props.translate('messages.downloadedDocument', {
              name: downloadingDoc.documentName,
            })}
          />
        )}
        {this.state.accessLevelChange && (
          <ConfirmationDialog
            theme={theme}
            title={this.props.translate(
              this.state.accessLevelChange.toFullAccess
                ? 'settingsViews.employees.upgradeToFullTitle'
                : 'settingsViews.employees.downgradeToLimitedTitle'
            )}
            question={this.props.translate(
              this.state.accessLevelChange.toFullAccess
                ? 'settingsViews.employees.upgradeToFullQuestion'
                : 'settingsViews.employees.downgradeToLimitedQuestion',
              { name: this.state.accessLevelChange.employee.fullName }
            )}
            cancelButton={this.props.translate('common.cancel')}
            acceptButton={this.props.translate(
              this.state.accessLevelChange.toFullAccess
                ? 'settingsViews.employees.upgradeToFull'
                : 'settingsViews.employees.downgradeToLimited'
            )}
            onCancel={this.onCancelChangeAccessLevel.bind(this)}
            onAccept={this.onConfirmChangeAccessLevel.bind(this)}
            loading={this.state.isChangingAccessLevel}
          />
        )}
        <TrainingManagerPermissionsDialog
          isOpen={this.state.isOpenTrainingManagerPermissionsDialog}
          onClose={() => this.setState({ isOpenTrainingManagerPermissionsDialog: false })}
          onAssign={this.onAssignTrainingManagerPermissions.bind(this)}
          trainingManagerConfigurationTypeId={
            this.state.actionEmployee?.trainingManagerConfigurationTypeId
          }
          isTrainingManager={this.state.actionEmployee?.isTrainingManager}
          isLoading={this.state.isAssignTrainingManagerLoading}
          isUnassignLoading={this.state.isUnassignTrainingManagerLoading}
        />

        {!showAddEditEmployeeView && (
          <BulkEmployeeActionBar
            translate={this.props.translate}
            employees={employees ? employees.employees : []}
            selectedRowIds={selectedRowIds as GridRowSelectionModel}
            onBulkActivate={this.onBulkActivate.bind(this)}
            onBulkDeactivate={this.onBulkDeactivate.bind(this)}
            onBulkUpgradeToFull={this.onBulkUpgradeToFull.bind(this)}
            onBulkDowngradeToLimited={this.onBulkDowngradeToLimited.bind(this)}
            onBulkResendInvite={this.onBulkResendInvite.bind(this)}
            theme={theme}
          />
        )}
        {showAccessLevelInfoDialog && (
          <EmployeeAccessLevelInfoDialog
            translate={this.props.translate}
            theme={theme}
            onClose={() => this.setState({ showAccessLevelInfoDialog: false })}
          />
        )}
        <EmployeeFilterDrawer
          open={showFilterDrawer}
          filters={employeeFilter}
          translate={this.props.translate}
          onClose={() => this.setState({ showFilterDrawer: false })}
          onApply={this.onApplyFilter.bind(this)}
        />
        {this.state.showAddEmployeeDialog && (
          <AddEmployeeDialog
            translate={this.props.translate}
            theme={theme}
            onClose={() => this.setState({ showAddEmployeeDialog: false })}
            onSave={this.onQuickAddEmployee.bind(this)}
            onChangeToFull={this.onChangeToFullAccess.bind(this)}
          />
        )}
      </div>
    );
  }
}

function CheckboxEmployeeTable(props: any) {
  const isAboveSm = useMediaQuery('(min-width:600px)');
  return <EmployeeTable {...props} checkboxSelection={isAboveSm} />;
}
export const SettingsEmployeesView = withLocale(SettingsEmployeesViewBase);
